import Fuse from "fuse.js";
import _, { get, lowerCase } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button, cn } from "@shared/ui";
import { Search } from "lucide-react";
import Carrier from "../components/Carrier";
import { inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import {
  CARRIER_LIST_LOAD_MORE_SCROLL_THRESHOLD,
  CREDIT_CARD_OPTION,
  EMPTY_CARRIERS,
  INITIAL_VISIBLE_CARRIERS,
  VISIBLE_CARRIER_BATCH_SIZE,
} from "../constants";

export const fuzzySearchCarriers = (fuse, lowerCaseKeyword, carriers) => {
  const fuseFilteredCarriers = fuse.search(lowerCaseKeyword);
  const fuseFilteredCarrierItems = fuseFilteredCarriers.map((carrier) => {
    return get(carrier, "item");
  });

  // Match substring of carrier name and domain
  const stringFilteredCarriers = carriers.filter((carrier) => {
    const carrierNameContainsKeyword = lowerCase(get(carrier, "name")).includes(
      lowerCaseKeyword
    );

    const carrierDomainContainsKeyword = lowerCase(
      get(carrier, "domain")
    ).includes(lowerCaseKeyword);
    return carrierNameContainsKeyword || carrierDomainContainsKeyword;
  });
  const results = [...stringFilteredCarriers, ...fuseFilteredCarrierItems];
  // Remove duplicates
  const uniqueResults = [...new Set(results)];
  return uniqueResults;
};

const footerLinkClassName =
  "inline-flex min-h-6 items-center rounded-sm text-balance text-center text-body-medium text-ink underline underline-offset-4 hover:no-underline";

const CreditCardOptions = ({ nextStep }) => {
  return (
    <button
      type="button"
      className={footerLinkClassName}
      onClick={() => nextStep("credit-cards")}
    >
      {CREDIT_CARD_OPTION}
    </button>
  );
};

const Carriers = ({
  step,
  nextStep,
  setCarrier,
  session,
  posthog,
  setCarriers,
  setIntegration,
}) => {
  const carriers = get(session, "carriers", EMPTY_CARRIERS);

  const manualEnabled = get(session, "config.manual.enabled");
  const manualCreditCardEnabled = get(session, "config.manual.creditCard");

  const [carrierName, setCarrierName] = useState("");
  const [filteredCarriers, setFilteredCarriers] = useState(carriers);
  const [visibleCarrierCount, setVisibleCarrierCount] = useState(
    INITIAL_VISIBLE_CARRIERS
  );
  const carrierListRef = useRef(null);

  // Build the Fuse index once (or whenever the carrier list changes) rather
  // than reconstructing it from scratch on every keystroke.
  const fuse = useMemo(
    () => new Fuse(carriers, { keys: ["name", "domain"], threshold: 0.4 }),
    [carriers]
  );

  // Run once on mount: record the pageview.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, []);

  useEffect(() => {
    setCarriers(carriers);
    setFilteredCarriers(carriers);
    setVisibleCarrierCount(INITIAL_VISIBLE_CARRIERS);
  }, [carriers, setCarriers]);

  const visibleCarriers = useMemo(
    () => filteredCarriers.slice(0, visibleCarrierCount),
    [filteredCarriers, visibleCarrierCount]
  );

  const loadMoreCarriers = () => {
    if (visibleCarrierCount >= filteredCarriers.length) {
      return;
    }

    setVisibleCarrierCount((currentCount) =>
      Math.min(
        currentCount + VISIBLE_CARRIER_BATCH_SIZE,
        filteredCarriers.length
      )
    );
  };

  const handleCarrierListScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    const remainingScrollDistance = scrollHeight - scrollTop - clientHeight;

    if (remainingScrollDistance <= CARRIER_LIST_LOAD_MORE_SCROLL_THRESHOLD) {
      loadMoreCarriers();
    }
  };

  const filter = (e) => {
    const keyword = e.target.value;

    if (keyword !== "") {
      const lowerCaseKeyword = lowerCase(keyword);
      const uniqueResults = fuzzySearchCarriers(
        fuse,
        lowerCaseKeyword,
        carriers
      );
      setFilteredCarriers(uniqueResults);
    } else {
      setFilteredCarriers(carriers);
    }

    setVisibleCarrierCount(INITIAL_VISIBLE_CARRIERS);
    if (carrierListRef.current) {
      carrierListRef.current.scrollTop = 0;
    }

    setCarrierName(keyword);
  };

  const hasResults = filteredCarriers && filteredCarriers.length > 0;
  const showCreditCardOption = manualEnabled && manualCreditCardEnabled;

  const onCarrierNotListed = () => {
    posthog.capture("my-carrier-is-not-listed", {
      carrier: carrierName,
    });
    manualEnabled ? nextStep("manual-account") : nextStep("exit-confirmation");
    setCarrier({ carrier: {} });
  };

  return (
    <>
      <StepBody ref={carrierListRef} onScroll={handleCarrierListScroll}>
        <StepHeading title="Find your insurance carrier" />
        {/* The search sits close to the list it filters. */}
        <div className="flex flex-col gap-3">
          <div className="relative">
            <label htmlFor="carrier-search" className="sr-only">
              Search carriers
            </label>
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-ink-secondary"
              strokeWidth={1.75}
            />
            <input
              id="carrier-search"
              placeholder="Search carriers"
              className={cn(inputClassName, "pl-10")}
              type="search"
              value={carrierName}
              onChange={filter}
            />
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            {hasResults
              ? `${filteredCarriers.length} ${filteredCarriers.length === 1 ? "carrier" : "carriers"} found`
              : "No results found"}
          </p>
          <ul className="filteredCarriers divide-y divide-subtle overflow-hidden rounded-lg border border-subtle">
            {hasResults ? (
              _(visibleCarriers)
                .chain()
                .map((carrier) => (
                  <li key={carrier.id}>
                    <Carrier
                      config={get(session, "config")}
                      carrier={carrier}
                      nextStep={nextStep}
                      step={step}
                      setCarrier={setCarrier}
                      setIntegration={setIntegration}
                    />
                  </li>
                ))
                .value()
            ) : (
              <li className="flex flex-col gap-1 px-4 py-4 text-body-lg ">
                <p className="font-medium text-ink">No results found.</p>
                <p className="text-ink-secondary">
                  Retry, or continue if your carrier isn't listed.
                </p>
              </li>
            )}
          </ul>
        </div>
      </StepBody>
      <StepFooter>
        {!hasResults && <Button onClick={onCarrierNotListed}>Continue</Button>}
        {(hasResults || showCreditCardOption) && (
          <nav
            aria-label="Other options"
            className={cn(
              "grid divide-x divide-subtle",
              hasResults && showCreditCardOption ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            {/* With no results, Continue above already covers "not listed". */}
            {hasResults && (
              <div className="flex justify-center px-2">
                <button
                  type="button"
                  className={footerLinkClassName}
                  onClick={onCarrierNotListed}
                >
                  My carrier isn’t listed
                </button>
              </div>
            )}
            {showCreditCardOption && (
              <div className="flex justify-center px-2">
                <CreditCardOptions nextStep={nextStep} />
              </div>
            )}
          </nav>
        )}
      </StepFooter>
    </>
  );
};

export default Carriers;
